import { useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import { useDebounce, useTranslate } from "@/hooks";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import { IDataElemItem } from "@/types/ses/sesDataElemTypes";
import { IActionDeclineData } from "@/types/ses/sesItemConfigTypes";
import { CustomTextField } from "@/ui";
import { NoOptionText, RenderOption } from "@/ui";
import { ISaveHandlerProps, IDeclineDataProps } from "./DeclineData.props";

const DeclineData = ({ action, idx, channel, isDisabled, handleSaveActionInStore }: IDeclineDataProps): JSX.Element => {
	const [inputDataElem, setInputDataElem] = useState<IDataElemItem | null>(null); // ЭД
	const [inputParam, setInputParam] = useState<string>(action.param || ""); // параметр (значение)

	const dataElemList = useAppSelector(selectDataList); // список ЭД

	const translate = useTranslate(); // hook для перевода текста
	const debouncedInputParam = useDebounce(inputParam, 1000); // задержка обработки значения

	useEffect(() => {
		const dataElementFound = dataElemList.data.find((dataElemItem) => dataElemItem.id === action.id);
		setInputDataElem(
			dataElementFound ||
				(action.id === ""
					? null
					: {
							id: action.id,
							name: `${action.id} (${translate("title_notFound")})`,
							type: "smc",
							rtype: "report",
							subtype: "class",
							weight: 1,
							hasNearestAction: false,
							hasScript: false,
							model: "",
							param: "",
					  })
		);
	}, []);

	// следим за значением
	useEffect(() => {
		if (debouncedInputParam !== action.param) {
			declineDataActionDataChangeHandler({ param: debouncedInputParam });
		}
	}, [debouncedInputParam]);

	// обработчик изменения ЭД
	const handleDataElemChange = (_e: React.SyntheticEvent<Element, Event>, value: IDataElemItem | null): void => {
		setInputDataElem(value);
		action.id !== (value?.id || "") && declineDataActionDataChangeHandler({ id: value?.id || "" });
	};

	// обработчик изменения данных действия "Забыть данные" (declinedata)
	const declineDataActionDataChangeHandler = ({ id, param }: Partial<ISaveHandlerProps>): void => {
		const data: { channel: string; idx: number; action: Partial<IActionDeclineData> } = {
			channel,
			idx,
			action: { id: id ?? (inputDataElem?.id || ""), param: param ?? inputParam },
		};
		handleSaveActionInStore(data);
	};

	return (
		<>
			{/* ЭД */}
			<Autocomplete
				fullWidth
				autoHighlight
				options={dataElemList.data}
				value={inputDataElem}
				onChange={handleDataElemChange}
				noOptionsText={<NoOptionText />}
				disabled={isDisabled}
				title={dataElemList.dictionary[inputDataElem?.id || ""]?.[0] || inputDataElem?.id}
				renderInput={(params) => <CustomTextField {...params} label={translate("input_dataElement")} error={!inputDataElem} />}
				getOptionLabel={(option) => option.name}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option.id}>
							{option.name}
						</RenderOption>
					);
				}}
			/>

			{/* параметр (значение) */}
			<CustomTextField
				label={translate("input_value")}
				value={inputParam}
				onChange={(e) => setInputParam(e.target.value)}
				disabled={isDisabled}
				title={inputParam}
				sx={{ minWidth: "120px" }}
			/>
		</>
	);
};

export default DeclineData;
